import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Description as DocIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Import services
import useNotificationStore from '../../stores/notificationStore';

/**
 * Client Export Dialog Component
 * Provides options to export client data in different formats
 */
const ClientExport = ({ open, onClose, client }) => {
  const theme = useTheme();
  const { showSuccess, showError } = useNotificationStore();
  
  // Export state
  const [exportFormat, setExportFormat] = useState('pdf');
  const [isExporting, setIsExporting] = useState(false);
  const [fileName, setFileName] = useState(`${client?.name || 'client'}-export`);
  
  // Fields to include in export
  const [selectedFields, setSelectedFields] = useState({
    basicInfo: true,
    contacts: true,
    address: true,
    serviceConfig: true,
    brands: true,
    campaigns: true,
    analytics: true,
  });
  
  // Handle format change
  const handleFormatChange = (event) => {
    setExportFormat(event.target.value);
  };
  
  // Handle field selection
  const handleFieldToggle = (field) => {
    setSelectedFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  // Handle select all fields
  const handleSelectAll = () => {
    const allSelected = Object.values(selectedFields).every(Boolean);
    const newValue = !allSelected;
    
    const newSelectedFields = {};
    Object.keys(selectedFields).forEach(field => {
      newSelectedFields[field] = newValue;
    });
    
    setSelectedFields(newSelectedFields);
  };
  
  // Handle export button click
  const handleExport = async () => {
    // Validate that at least one field is selected
    if (!Object.values(selectedFields).some(Boolean)) {
      showError('Please select at least one section to export');
      return;
    }
    
    setIsExporting(true);
    
    try {
      // This would be replaced with actual export API call
      // For now, simulate with timeout
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show success message
      showSuccess(`Client data exported as ${exportFormat.toUpperCase()} successfully`);
      
      // Close dialog
      onClose();
    } catch (error) {
      showError('Export failed: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={!isExporting ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Export Client Data</Typography>
        {!isExporting && (
          <Button 
            onClick={onClose} 
            color="inherit" 
            size="small" 
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        )}
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Export Format
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              name="export-format"
              value={exportFormat}
              onChange={handleFormatChange}
            >
              <FormControlLabel 
                value="pdf" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PdfIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                    PDF Document
                  </Box>
                } 
              />
              <FormControlLabel 
                value="csv" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CsvIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                    CSV Spreadsheet
                  </Box>
                } 
              />
              <FormControlLabel 
                value="docx" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DocIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    Word Document
                  </Box>
                } 
              />
            </RadioGroup>
          </FormControl>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            File Name
          </Typography>
          <TextField
            fullWidth
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            InputProps={{
              endAdornment: <Typography variant="body2" color="textSecondary">{`.${exportFormat}`}</Typography>
            }}
          />
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1">
              Sections to Include
            </Typography>
            <Button 
              size="small" 
              onClick={handleSelectAll}
            >
              {Object.values(selectedFields).every(Boolean) ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.basicInfo}
                  onChange={() => handleFieldToggle('basicInfo')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Basic Information" 
                secondary="Name, description, industry, region, status, etc."
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.contacts}
                  onChange={() => handleFieldToggle('contacts')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Contact Information" 
                secondary="Primary and additional contacts"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.address}
                  onChange={() => handleFieldToggle('address')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Address Information" 
                secondary="Physical and billing addresses"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.serviceConfig}
                  onChange={() => handleFieldToggle('serviceConfig')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Service Configuration" 
                secondary="Platforms, services, posting frequency, etc."
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.brands}
                  onChange={() => handleFieldToggle('brands')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Brands" 
                secondary="List of associated brands"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.campaigns}
                  onChange={() => handleFieldToggle('campaigns')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Campaigns" 
                secondary="List of marketing campaigns"
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <Checkbox
                  checked={selectedFields.analytics}
                  onChange={() => handleFieldToggle('analytics')}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Analytics" 
                secondary="Performance metrics and statistics"
              />
            </ListItem>
          </List>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={isExporting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientExport;